import axios, { AxiosInstance } from "axios";
import { generateBaseUrl } from "./generateBaseUrl";
import { useApiStore } from "./store";

export const enum ClientType {
  Base = "base",
  Tenant = "tenant",
  Portfolio = "portfolio",
}

export type ApiRoute =
  | string
  | null
  | undefined
  | (() => string | null | undefined);
export type ApiBatchRoute =
  | ApiRoute[]
  | (() => string[] | null | undefined)
  | null
  | undefined;

export function createClient(token: string): AxiosInstance {
  return axios.create({
    baseURL: generateBaseUrl(),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function unwrapRoute(route: ApiRoute): string {
  if (!route) return "";
  if (typeof route === "function") return route()?.trim() ?? "";
  return route.trim();
}

export function unwrapRoutes(routes: ApiBatchRoute): string[] {
  if (!routes) return [];
  if (typeof routes === "function")
    return (routes() ?? [])
      .map((route) => route.trim())
      .filter((route) => !!route);
  return routes.map(unwrapRoute).filter((route) => !!route);
}

export function client() {
  return useApiStore.getState().client;
}

export function generateRoute(route: ApiRoute, clientType: ClientType) {
  switch (clientType) {
    case ClientType.Portfolio:
      return portfolioRoute(route);
    case ClientType.Tenant:
      return tenantRoute(route);
    default:
      return baseRoute(route);
  }
}

export function generateRoutes(routes: ApiBatchRoute, clientType: ClientType) {
  return unwrapRoutes(routes).map((route) => generateRoute(route, clientType));
}

function portfolioRoute(route: ApiRoute) {
  const portfolioId = useApiStore.getState().portfolioId!.toString();
  if (!unwrapRoute(route)) return `/portfolios/${portfolioId}`;
  return `/portfolios/${portfolioId}/${unwrapRoute(route)}`;
}

function tenantRoute(route: ApiRoute) {
  const tenantId = useApiStore.getState().tenantId!.toString();
  if (!unwrapRoute(route)) return `/tenants/${tenantId}`;
  return `/tenants/${tenantId}/${unwrapRoute(route)}`;
}

function baseRoute(route: ApiRoute) {
  if (!unwrapRoute(route)) return "";
  return `/${unwrapRoute(route)}`;
}
